import React, { useState } from 'react'
import { Button } from '@/components/ui/Button'
import type { ProductionOrder } from '@/services/productionOrders'

export interface ReturnToDraftModalProps {
  isOpen: boolean
  order: ProductionOrder | null
  onClose: () => void
  onConfirm: (reason: string) => Promise<void>
}

export function ReturnToDraftModal({
  isOpen,
  order,
  onClose,
  onConfirm,
}: ReturnToDraftModalProps): React.ReactElement | null {
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (!isOpen || !order) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = reason.trim()
    if (!trimmed) {
      setError('Vui lòng nhập lý do hoàn dòng về Nháp.')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      await onConfirm(trimmed)
      setReason('')
      onClose()
    } catch (err: any) {
      setError(err?.message || 'Có lỗi xảy ra khi hoàn dòng về Nháp.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-slate-900/60 p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-3.5">
          <h3 className="font-bold text-slate-800 text-base">
            Hoàn dòng về Nháp: {order.variant_code}
          </h3>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-600 text-lg leading-none"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <p className="text-sm text-slate-600">
            Dòng lệnh sản xuất cho sản phẩm <strong className="font-mono text-slate-800">{order.variant_code}</strong> sẽ được chuyển về trạng thái Nháp. Vui lòng nhập lý do để lưu lịch sử:
          </p>

          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
              Lý do hoàn về Nháp <span className="text-rose-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value)
                if (error) setError(null)
              }}
              placeholder="Nhập lý do chi tiết..."
              rows={3}
              className="w-full rounded border border-slate-300 p-2.5 text-sm text-slate-900 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              autoFocus
            />
            {error && <p className="mt-1 text-xs text-rose-600 font-medium">{error}</p>}
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="secondary" size="sm" type="button" onClick={onClose} disabled={submitting}>
              Hủy bỏ
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={submitting || !reason.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {submitting ? 'Đang xử lý...' : 'Xác nhận hoàn về Nháp'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
